/**
 * @file HeaderSection.jsx
 * @author Archana
 * @date 2024-10-29
 * @description Header component of lifecycle.
 */
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // Import MUI green tick icon

export const DetailsPanel = ({
  isLoading,
  lead,
  candidateEvents,
  toggleDrawer,
  candidateuuid,
}) => {
  let i = 3;
  return (
    <>
      {isLoading ? (
        <Skeleton variant="rounded" width={180} height={24} />
      ) : (
        <Breadcrumbs aria-label="breadcrumb">
          <Link to="/candidates">Candidate</Link>
          <Typography sx={{ color: "text.primary" }}>Lifecycle</Typography>
        </Breadcrumbs>
      )}

      <Stack display="flex" direction={{ xs: "column", md: "row" }} rowGap={2}>
        <Stack flex={1}>
          {isLoading ? (
            <Skeleton
              sx={{ mt: 3 }}
              variant="rounded"
              width={390}
              height={24}
            />
          ) : (
            <Typography
              sx={{ mt: 2 }}
              fontWeight={700}
              fontSize={24}
              color="#222222"
            >
              Candidate Lifecycle - {lead?.name || "Lead Name"}
            </Typography>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton
            sx={{ alignSelf: "end", mb: 1 }}
            variant="rounded"
            width={350}
            height={30}
          />
        ) : (
          <Stack
            display="flex"
            spacing={1}
            direction="row"
            alignSelf={{ xs: "start", md: "end" }}
            pb={1}
          >
            <Button variant="outlined" onClick={toggleDrawer}>
              Go to Documents
            </Button>
          </Stack>
        )}
      </Stack>

      {!isLoading && <Divider />}
      <div
        className="main-container1"
        style={{ width: "100%", overflowY: "scroll", overflowX: "inherit" }}
      >
        {isLoading ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            flexWrap="wrap"
            mt={1}
          >
            <Skeleton
              sx={{ mt: 0 }}
              variant="rounded"
              width={690}
              height={900}
              mt={1}
              mb={2}
            />
          </Stack>
        ) : (
          <Box
            component={"div"}
            className="ly-chart-wrapper-agent"
            padding={{ xs: 0, md: "20px" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              flexWrap="wrap"
              mt={1}
            >
              <div style={{ position: "relative", zIndex: 2 }}>
                <svg
                  width={109}
                  height={137}
                  viewBox="0 0 109 137"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fill="#3A80F7" d="M0 0H109V104H0z" />
                  <text
                    x="50%"
                    y="50%"
                    fill="#fff"
                    fontSize="50px"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    1
                  </text>
                  <path d="M72 104H0l72 33v-33z" fill="#1A3A6F" />
                </svg>
              </div>

              {/* Middle white box with paragraph */}
              <div className="cand-lifecycle-event-box-style">
                <span className="cand-lifecycle-event">
                  Converted to candidate
                </span>
                <CheckCircleIcon className="ly-tick-icon" />
              </div>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              flexWrap="wrap"
              mt={1}
            >
              <div style={{ position: "relative", zIndex: 2 }}>
                <svg
                  width={109}
                  height={137}
                  viewBox="0 0 109 137"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fill="#3A80F7" d="M0 0H109V104H0z" />
                  <text
                    x="50%"
                    y="50%"
                    fill="#fff"
                    fontSize="50px"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    2
                  </text>
                  <path d="M72 104H0l72 33v-33z" fill="#1A3A6F" />
                </svg>
              </div>

              {/* Middle white box with paragraph */}
              <div className="cand-lifecycle-event-box-style">
                <span className="cand-lifecycle-event">Documents</span>
                <Button
                  style={{
                    borderRadius: "20px",
                    border: "0.5px solid var(--Border-Primary, #3A80F7)",
                    background: "var(--Bg-Tonal-Blue, #E1ECFE)",
                    color: "var(--Text-Tonal-Blue, #3A80F7)",
                    textAlign: "center",
                    fontSize: "var(--TypeStyle-H5, 18px)",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "var(--TypeHeight-H5, 29px)",
                    padding: "10px 20px", // You can adjust padding as needed
                    cursor: "pointer",
                    textTransform: "none",
                  }}
                  onClick={toggleDrawer}
                >
                  Upload
                </Button>
              </div>
            </Stack>
            {candidateEvents.map((option) => (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={2}
                flexWrap="wrap"
                mt={1}
                key={option.id}
              >
                <div style={{ position: "relative", zIndex: 2 }}>
                  <svg
                    width={109}
                    height={137}
                    viewBox="0 0 109 137"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="#3A80F7" d="M0 0H109V104H0z" />
                    <text
                      x="50%"
                      y="50%"
                      fill="#fff"
                      fontSize="50px"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {i}
                    </text>
                    {i++}
                    <path d="M72 104H0l72 33v-33z" fill="#1A3A6F" />
                  </svg>
                </div>

                {/* Middle white box with paragraph */}
                <div className="cand-lifecycle-event-box-style">
                  <span className="cand-lifecycle-event">
                    {option.lifecycle_name}
                  </span>
                  <CheckCircleIcon className="ly-tick-icon" />
                </div>
              </Stack>
            ))}
          </Box>
        )}
      </div>
    </>
  );
};
